import {
  collection,
  doc,
  serverTimestamp,
  writeBatch
} from 'firebase/firestore';
import { COA_TEMPLATES, type CoaTemplateId } from '@finvox/shared';
import { getFirestoreDb } from '@/lib/firebase';
import { FIRESTORE_OP_TIMEOUT_MS, withTimeout } from '@/lib/withTimeout';

export async function createOrganization(
  uid: string,
  email: string,
  name: string,
  templateId: CoaTemplateId
): Promise<string> {
  const db = getFirestoreDb();
  const orgRef = doc(collection(db, 'organizations'));
  const orgId = orgRef.id;
  const template = COA_TEMPLATES[templateId];
  const year = new Date().getFullYear();
  const periodId = `fy_${year}`;
  // Phase 1: org + membership (rules for subcollections require member or owner doc to exist)
  const coreBatch = writeBatch(db);
  coreBatch.set(orgRef, {
    name,
    countryTemplate: templateId,
    ownerId: uid,
    createdAt: serverTimestamp()
  });
  coreBatch.set(doc(db, 'organizations', orgId, 'members', uid), {
    role: 'owner',
    email,
    uid,
    joinedAt: serverTimestamp()
  });
  coreBatch.set(
    doc(db, 'users', uid),
    {
      defaultOrgId: orgId,
      email,
      memberships: [{ orgId, orgName: name, role: 'owner' }],
      updatedAt: serverTimestamp()
    },
    { merge: true }
  );
  await withTimeout(coreBatch.commit(), FIRESTORE_OP_TIMEOUT_MS, 'createOrganization');

  const batch = writeBatch(db);

  batch.set(doc(db, 'organizations', orgId, 'settings', 'main'), {
    currentPeriodId: periodId,
    fiscalYearStartMonth: 1
  });

  batch.set(doc(db, 'organizations', orgId, 'settings', 'regional'), {
    showHijri: templateId === 'sa' || templateId === 'eg',
    branchCode: 'HQ',
    countryPack: templateId
  });

  batch.set(doc(db, 'organizations', orgId, 'settings', 'security'), {
    mfaPolicy: 'optional',
    ssoEnabled: false
  });

  batch.set(doc(db, 'organizations', orgId, 'settings', 'dimensions'), {
    costCenterRequired: false
  });

  batch.set(doc(db, 'organizations', orgId, 'settings', 'reportSchedule'), {
    enabled: false,
    email: '',
    reportType: 'trial_balance',
    dayOfWeek: 1
  });

  batch.set(doc(db, 'organizations', orgId, 'settings', 'tax'), {
    vatOutputAccountCode: '2200',
    vatInputAccountCode: '2201'
  });

  batch.set(doc(db, 'organizations', orgId, 'settings', 'docSeries'), {
    salesInvoice: { prefix: 'INV', branch: 'HQ', year, next: 1 },
    purchaseBill: { prefix: 'BILL', branch: 'HQ', year, next: 1 }
  });

  batch.set(doc(db, 'organizations', orgId, 'dashboardStats', 'summary'), {
    revenueMtd: '0.00',
    cashBalance: '0.00',
    taxLiability: '0.00',
    draftJournalCount: 0,
    draftInvoiceCount: 0,
    draftBillCount: 0,
    openImportJobs: 0,
    updatedAt: new Date().toISOString()
  });

  batch.set(doc(db, 'organizations', orgId, 'fiscalPeriods', periodId), {
    name: `FY ${year}`,
    startDate: `${year}-01-01`,
    endDate: `${year}-12-31`,
    status: 'open',
    createdAt: serverTimestamp()
  });

  for (const acc of template.accounts) {
    const accRef = doc(collection(db, 'organizations', orgId, 'accounts'));
    batch.set(accRef, {
      code: acc.code,
      name: acc.name,
      nameAr: acc.nameAr,
      type: acc.type,
      active: true,
      createdAt: serverTimestamp()
    });
  }

  await withTimeout(batch.commit(), FIRESTORE_OP_TIMEOUT_MS, 'createOrganization');
  return orgId;
}
